import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { workAuthService } from '@/services/workAuthService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  CheckCircle,
  XCircle
} from 'lucide-react';

interface AuditLog {
  id: string;
  workAuthId: string;
  action: string;
  columnName: string;
  oldValue: string | null;
  newValue: string;
  smartsheetRowId: string;
  performedBy: string;
  success: boolean;
  errorMessage: string | null;
  createdAt: string;
  performer: { id: string; email: string; name: string } | null;
  workAuth: {
    id: string;
    employee: { employeeId: string; firstName: string; lastName: string };
  } | null;
}

interface AuditLogsData {
  logs: AuditLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const SmartsheetAuditLogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<AuditLogsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const limit = 50;

  const fetchData = async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await workAuthService.getSmartsheetAuditLogs(pageNum, limit);
      if (response.success) {
        setData(response.data);
      } else {
        setError('Failed to load audit logs');
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page);
  }, [page]);

  const handleRefresh = () => {
    fetchData(page);
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (data && page < data.pagination.totalPages) {
      setPage(page + 1);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatAction = (action: string) => {
    return action.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
  };

  if (loading && !data) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/hr/work-auth/dashboard')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-6 w-6 text-green-600" />
            <div>
              <CardTitle className="text-xl">Smartsheet Audit Logs</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                All writes to Smartsheet from the system
                {data && ` (${data.pagination.total.toLocaleString()} total entries)`}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {data && data.logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No audit logs yet</p>
              <p className="text-sm mt-1">Writes to Smartsheet will appear here</p>
            </div>
          ) : data && (
            <>
              {/* Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                          Date/Time
                        </th>
                        <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                          Employee
                        </th>
                        <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                          Action
                        </th>
                        <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                          Column
                        </th>
                        <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                          Old Value
                        </th>
                        <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                          New Value
                        </th>
                        <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                          Performed By
                        </th>
                        <th className="px-3 py-3 text-left font-medium text-muted-foreground">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {data.logs.map((log) => (
                        <tr
                          key={log.id}
                          className="hover:bg-muted/30 transition-colors"
                        >
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatDate(log.createdAt)}
                          </td>
                          <td className="px-3 py-2">
                            {log.workAuth ? (
                              <div>
                                <div className="font-medium">
                                  {log.workAuth.employee.firstName} {log.workAuth.employee.lastName}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {log.workAuth.employee.employeeId}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline">
                              {formatAction(log.action)}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {log.columnName}
                          </td>
                          <td className="px-3 py-2">
                            {log.oldValue || <span className="text-muted-foreground">-</span>}
                          </td>
                          <td className="px-3 py-2 font-medium">
                            {log.newValue}
                          </td>
                          <td className="px-3 py-2">
                            {log.performer ? (
                              <span title={log.performer.email}>
                                {log.performer.name}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">System</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {log.success ? (
                              <div className="flex items-center gap-1 text-green-600">
                                <CheckCircle className="h-4 w-4" />
                                <span>Success</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-red-600" title={log.errorMessage || ''}>
                                <XCircle className="h-4 w-4" />
                                <span>Failed</span>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, data.pagination.total)} of {data.pagination.total.toLocaleString()} entries
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousPage}
                    disabled={page === 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">
                    Page {page} of {data.pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={page >= data.pagination.totalPages || loading}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SmartsheetAuditLogsPage;
